import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FaImage, FaVideo, FaPlus, FaGripLines, FaTimes } from 'react-icons/fa';
import { 
  selectAllImages, 
  selectAllVideos,
  removeContent
} from '../../store/slices/uploadedContentSlice';
import ImageGallery from '../ImageGallery/ImageGallery';
import './SwipeableBottomSheet.css';

const SwipeableBottomSheet = ({ onUploadClick }) => {
  const dispatch = useDispatch();
  const images = useSelector(selectAllImages);
  const videos = useSelector(selectAllVideos);
  const allContent = [...images, ...videos].sort((a, b) => 
    new Date(b.uploadedAt) - new Date(a.uploadedAt)
  );

  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const sheetRef = useRef(null);

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const newY = e.touches[0].clientY;
    setCurrentY(newY);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    const deltaY = startY - currentY;
    const threshold = 50; // minimum swipe distance

    if (deltaY > threshold) {
      // Swiped up - expand
      setIsExpanded(true);
    } else if (deltaY < -threshold) {
      // Swiped down - collapse
      setIsExpanded(false);
    }

    setIsDragging(false);
    setStartY(0);
    setCurrentY(0);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartY(e.clientY);
    setCurrentY(e.clientY);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const newY = e.clientY;
    setCurrentY(newY);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    const deltaY = startY - currentY;
    const threshold = 50;

    if (deltaY > threshold) {
      setIsExpanded(true);
    } else if (deltaY < -threshold) {
      setIsExpanded(false);
    }

    setIsDragging(false);
    setStartY(0);
    setCurrentY(0);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers are stable; adding them would re-attach every render
  }, [isDragging, startY, currentY]);

  // Hide the sheet if there are no uploaded items
  if (allContent.length === 0) {
    return null;
  }

  const openGallery = (index = 0) => {
    setSelectedIndex(index);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  const handleRemoveItem = (e, item) => {
    e.stopPropagation(); // Prevent opening gallery when clicking X
    dispatch(removeContent({ id: item.id, type: item.type }));
  };

  return (
    <>
      <div 
        ref={sheetRef}
        className={`swipeable-bottom-sheet ${isExpanded ? 'expanded' : 'minimized'} ${isDragging ? 'dragging' : ''}`}
      >
        {/* Draggable upper section - includes handle and header */}
        <div 
          className="sheet-draggable-area"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          <div className="sheet-handle">
            <div className="handle-bar">
              <FaGripLines />
            </div>
          </div>

          <div className="sheet-header">
            <h3 className="sheet-title">
              الصور والفيديوهات ({allContent.length})
            </h3>
            <div className="header-buttons">
              <button 
                className="upload-more-button"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering drag when clicking button
                  onUploadClick();
                }}
                onMouseDown={(e) => e.stopPropagation()} // Prevent drag start on button
                onTouchStart={(e) => e.stopPropagation()} // Prevent drag start on button
                aria-label="إضافة"
              >
                <FaPlus />
                <span>إضافة</span>
              </button>
            </div>
          </div>
        </div>

        <div className="sheet-content">
          <div className="uploaded-content-grid">
            {allContent.map((item, index) => (
              <div 
                key={item.id} 
                className="uploaded-item"
                onClick={() => openGallery(index)}
              >
                {item.type === 'image' ? (
                  <img 
                    src={item.thumbUrl || item.url} 
                    alt={item.name || 'صورة مرفوعة'} 
                    className="uploaded-media"
                  />
                ) : (
                  <video 
                    src={item.thumbUrl || item.url} 
                    className="uploaded-media"
                  />
                )}
                <div className="uploaded-item-overlay">
                  {item.type === 'image' ? (
                    <FaImage className="media-icon" />
                  ) : (
                    <FaVideo className="media-icon" />
                  )}
                </div>
                <button
                  className="remove-item-button"
                  onClick={(e) => handleRemoveItem(e, item)}
                  aria-label="إزالة"
                >
                  <FaTimes />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ImageGallery 
        content={allContent}
        isOpen={isGalleryOpen}
        onClose={closeGallery}
        initialIndex={selectedIndex}
      />
    </>
  );
};

export default SwipeableBottomSheet;
